import classNames from 'classnames'
import React, { useEffect, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Input } from 'core-components/library/inputs'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Actions } from 'core-components/library/modalDialog'
import { TPopupHorizontal } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { useUserStore } from 'core/hooks/services/useUserStore'

import { ContactCodeLogs } from '../contactLogButton/ContactCodeLogs'

import { TOOLS_REQUIRED_HPID } from './configs'
import { IAbilityObject, ICurrentVariant } from './models'

import styles from './VariantSelector.module.scss'

declare interface ISelectedValue {
  checked: boolean
  id: string
}

interface IVariantSelector {
  contactCodes: ICurrentVariant[]
  contactCodesRequests: ICurrentVariant[]
  abilityValues: IAbilityObject
  setUpdateData: (e: boolean) => void
  savedData: ICurrentVariant[]
  setSavedData: (newData: ICurrentVariant[]) => void
  contactId: string
  type: string
  readOnly: boolean
  HPID?: string
  checkFn?: (value: string) => string
}

enum excludedStatus {
  Done = 'Done',
  Rejected = 'Rejected',
}

const excludedStatusList = [excludedStatus.Done, excludedStatus.Rejected]

const VariantSelector = ({
  contactCodes,
  contactCodesRequests,
  abilityValues,
  setUpdateData,
  savedData,
  setSavedData,
  contactId,
  type,
  readOnly,
  HPID,
  checkFn,
}: IVariantSelector) => {
  const isSuperAdmin = useUserStore().isAdminUser()

  const [isLoading, setLoading] = useState(false)
  const [isOpenAudit, setOpenAudit] = useState(false)

  const [possibleValues, setPossibleValues] = useState<{ id: string; disabled: boolean }[]>([])
  const [currentValues, setCurrentValues] = useState<ICurrentVariant[]>([])

  const [selectedCV, setSelectedCV] = useState<ISelectedValue[]>([])
  const [selectedAV, setSelectedAV] = useState<ISelectedValue[]>([])

  const [filterCV, setFilterCV] = useState<string>('')
  const [filterAV, setFilterAV] = useState<string>('')

  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ key: string; value: string }[]>([])

  useEffect(() => {
    const newPossibleCodes = Object.keys(abilityValues).filter(
      (el) => el && !currentValues.find((value) => value.value.id === el)
    )
    setPossibleValues(
      newPossibleCodes.map((code) => {
        return {
          id: code,
          disabled: !abilityValues[code],
        }
      })
    )
  }, [abilityValues, currentValues])

  useEffect(() => {
    if (savedData) {
      setCurrentValues(savedData)
    } else {
      let data: ICurrentVariant[] = contactCodes.length ? contactCodes.slice() : []

      if (contactCodesRequests.length) {
        contactCodesRequests.forEach((item) => {
          const { status = '', action = '' } = item

          if (status !== excludedStatus.Done || action !== 'Delete') {
            data = data.filter((v) => v.value.id !== item.value.id)
            data.push({ ...item })
          }
        })
      }
      setCurrentValues(data)
    }
  }, [contactCodes, contactCodesRequests])

  useEffect(() => {
    setSelectedCV(
      currentValues.map((item) => ({
        id: item.value.id,
        checked: false,
      }))
    )
    setUpdateData(
      !!currentValues.filter(
        (el) =>
          el.status &&
          el.action &&
          !contactCodesRequests.find(
            (v) => v.value.id === el.value.id && v.status === el.status && v.action === el.action
          )
      ).length ||
        !!contactCodesRequests.filter(
          (el) =>
            el.status &&
            el.action &&
            !currentValues.find(
              (v) => v.value.id === el.value.id && v.status === el.status && v.action === el.action
            )
        ).length
    )

    setSavedData(currentValues)
    setFilterCV('')
  }, [currentValues, contactCodesRequests])

  useEffect(() => {
    setSelectedAV(
      possibleValues.map((item) => ({
        id: item.id,
        checked: false,
      }))
    )
    setFilterAV('')
  }, [possibleValues])

  const confirmationDialog = () => {
    return (
      <ModalDialog
        size={'xs'}
        show={showDialog}
        onClose={() => {
          setShowDialog(false)
        }}
        content={() => (
          <div className={styles.dialog}>
            User has not completed the HPID registration
            <Button
              onClick={() => {
                setShowDialog(false)
              }}
              color={'primary'}
            >
              OK
            </Button>
          </div>
        )}
      />
    )
  }

  return (
    <div className={styles.root}>
      {confirmationDialog()}
      <Header
        setLoading={setLoading}
        currentValues={currentValues}
        setCurrentValues={setCurrentValues}
        readOnly={readOnly}
        selectedCV={selectedCV}
        setOpenAudit={setOpenAudit}
        filterCV={filterCV}
        setFilterCV={setFilterCV}
        selectedAV={selectedAV}
        errors={errors}
        filterAV={filterAV}
        setFilterAV={setFilterAV}
      />

      <div className={styles.body}>
        <div className={styles.body__group}>
          {isLoading ? (
            <Loader />
          ) : (
            <ListCurrent
              currentValues={currentValues}
              setCurrentValues={setCurrentValues}
              filterCV={filterCV}
              readOnly={readOnly}
              isSuperAdmin={isSuperAdmin}
              abilityValues={abilityValues}
              selectedCV={selectedCV}
              setSelectedCV={setSelectedCV}
              contactCodes={contactCodes}
            />
          )}
        </div>
        <div className={styles.body__group}>
          {isLoading ? (
            <Loader />
          ) : (
            <ListPossible
              possibleValues={possibleValues}
              filterAV={filterAV}
              errors={errors}
              setErrors={setErrors}
              readOnly={readOnly}
              isSuperAdmin={isSuperAdmin}
              selectedAV={selectedAV}
              setSelectedAV={setSelectedAV}
              HPID={HPID}
              checkFn={checkFn}
              setShowDialog={setShowDialog}
            />
          )}
        </div>
      </div>

      <ModalDialog
        show={isOpenAudit}
        onClose={() => setOpenAudit(false)}
        size={'xl'}
        content={ContactCodeLogs}
        contentProps={{
          contactId,
          type,
          onClose: () => setOpenAudit(false),
        }}
        title={'Audit trail'}
        header={() => (
          <Actions
            position={'end'}
            items={[{ id: 'close', label: 'OK', onClick: () => setOpenAudit(false) }]}
          />
        )}
      />
    </div>
  )
}

interface IHeaderProps {
  setLoading: (val: boolean) => void
  currentValues: ICurrentVariant[]
  setCurrentValues: (val: ICurrentVariant[]) => void
  readOnly: boolean
  selectedCV: ISelectedValue[]
  setOpenAudit: (val: boolean) => void
  filterCV: string
  setFilterCV: (val: string) => void
  selectedAV: ISelectedValue[]
  errors: { key: string; value: string }[]
  filterAV: string
  setFilterAV: (val: string) => void
}
const Header = ({
  setLoading,
  currentValues,
  setCurrentValues,
  readOnly,
  selectedCV,
  setOpenAudit,
  filterCV,
  setFilterCV,
  selectedAV,
  errors,
  filterAV,
  setFilterAV,
}: IHeaderProps) => {
  const removeCurrentValue = () => {
    setLoading(true)
    let newCurrentValues: ICurrentVariant[] = currentValues.slice()
    selectedCV.forEach((item) => {
      const data = currentValues.find((v) => v.value.id === item.id)
      if (item.checked && data && data.status !== 'WIP' && data.action !== 'Delete') {
        newCurrentValues = newCurrentValues.filter((item) => item.value.id !== data.value.id)
        if (!(data.status === 'New' && data.action === 'Add') && data.status !== 'Rejected') {
          const newValue = {
            value: {
              id: data.value.id,
              caption: data.value.caption,
            },
            status: 'New',
            comment: data.comment,
            action: 'Delete',
            lastActionDate: new Date(),
          }

          newCurrentValues.push(newValue)
        }
      }
    })
    setCurrentValues(newCurrentValues)
    setLoading(false)
  }

  const addCurrentValue = () => {
    setLoading(true)
    let newCurrentValues: ICurrentVariant[] = currentValues.slice()
    selectedAV.forEach((item) => {
      const code = item.id
      if (code && item.checked) {
        const currentValue = currentValues.find((item) => item.value.id === code)

        if (!currentValue) {
          const newValue = {
            value: {
              id: code,
              caption: code,
            },
            status: 'New',
            comment: '',
            action: 'Add',
            lastActionDate: new Date(),
          }

          newCurrentValues.push(newValue)
        } else if (currentValue.action === 'Delete' && currentValue.status === 'New') {
          const newValue = {
            value: {
              id: code,
              caption: code,
            },
            status: 'New',
            comment: currentValue.comment,
            action: 'Add',
            lastActionDate: new Date(),
          }

          newCurrentValues = newCurrentValues.filter((item) => item.value.id !== code)
          newCurrentValues.push(newValue)
        }
      }
    })

    setCurrentValues(newCurrentValues)
    setLoading(false)
  }
  return (
    <header className={styles.header}>
      <div className={styles.header__group}>
        <Button
          className={styles.header__button}
          color={'danger'}
          disabled={readOnly || !selectedCV.find((item) => item.checked === true)}
          onClick={removeCurrentValue}
        >
          Remove
        </Button>
        <Button className={styles.header__button} onClick={() => setOpenAudit(true)}>
          Audit trail
        </Button>
        <Input
          classes={{
            root: styles.header__input,
          }}
          label="Search"
          type="search"
          endContent={<i className={'kbase kbase-search'} />}
          value={filterCV}
          onChange={(val) => setFilterCV(val)}
        />
      </div>

      <div className={styles.header__group}>
        <Button
          className={classNames(styles.header__button, styles['header__button--success'])}
          disabled={
            readOnly || !selectedAV.find((item) => item.checked === true) || !!errors.length
          }
          onClick={addCurrentValue}
        >
          Add
        </Button>
        <Input
          classes={{
            root: styles.header__input,
          }}
          label="Search"
          type="search"
          endContent={<i className={'kbase kbase-search'} />}
          value={filterAV}
          onChange={(val) => setFilterAV(val)}
        />
      </div>
    </header>
  )
}

interface IListCurrentProps {
  currentValues: ICurrentVariant[]
  setCurrentValues: (val: ICurrentVariant[]) => void
  filterCV: string
  readOnly: boolean
  isSuperAdmin: boolean
  abilityValues: IAbilityObject
  selectedCV: ISelectedValue[]
  setSelectedCV: (val: ISelectedValue[]) => void
  contactCodes: ICurrentVariant[]
}
const ListCurrent = ({
  currentValues,
  setCurrentValues,
  filterCV,
  readOnly,
  isSuperAdmin,
  abilityValues,
  selectedCV,
  setSelectedCV,
  contactCodes,
}: IListCurrentProps) => {
  const getStylesCardCV = (i: number) => {
    const status = currentValues[i].status
    const action = currentValues[i].action
    if (status === 'WIP') {
      return styles['item--wip']
    } else if (status === 'New') {
      return styles['item--new']
    } else if (status === 'Done') {
      return action === 'Delete' ? styles['item--hide'] : ''
    } else if (status === 'Rejected') {
      return styles['item--rejected']
    }
    return ''
  }

  return (
    <div className={styles.list}>
      <div className={styles.list__scroller}>
        {currentValues
          .filter((item) =>
            filterCV ? ~item.value.id.toUpperCase().indexOf(filterCV.toUpperCase()) : true
          )
          .map((item, index) => {
            let date
            if (item.lastActionDate && item.lastActionDate.toISOString) {
              date = item.lastActionDate.toISOString()
              date = date.substring(0, date.indexOf('.'))
            } else {
              date = item.lastActionDate
            }

            let label = ''
            if (date) {
              if (item.status === 'New') {
                label = item.action === 'Delete' ? `(deleted ${date})` : `(added ${date})`
              } else if (item.status === 'WIP') {
                label =
                  item.action === 'Delete'
                    ? `(deleted, WIP from ${date})`
                    : `(added, WIP from ${date})`
              } else if (item.status === 'Rejected') {
                label =
                  item.action === 'Delete'
                    ? `(deleted, Rejected ${date})`
                    : `(added, Rejected ${date})`
              }
            }
            return (
              <div className={classNames(styles.item, getStylesCardCV(index))} key={index}>
                <div className={styles.item__check}>
                  <Checkbox
                    disabled={
                      readOnly ||
                      (!isSuperAdmin && (!abilityValues[item.value.id] || item.status === 'WIP'))
                    }
                    onChange={() => {
                      const newArray = selectedCV.filter((v) => v.id !== item.value.id)
                      const el = selectedCV.find((v) => v.id === item.value.id)
                      if (el) {
                        el.checked = !el.checked
                        newArray.push(el)
                        setSelectedCV(newArray)
                      }
                    }}
                    checked={!!selectedCV.find((v) => v.id === item.value.id && v.checked)}
                  />
                </div>
                <div
                  className={classNames(
                    styles.item__label,
                    styles.ellipsis,
                    item.action === 'Delete' ? styles['item__label--delete'] : ''
                  )}
                >
                  {item.value.id}
                </div>
                <div className={styles.item__actions}>
                  {item.status === 'New' ||
                  (item.status === 'Rejected' && item.action === 'Delete') ? (
                    <Button
                      variant={'text'}
                      disabled={readOnly}
                      onClick={() => {
                        const tempCurrentValues = currentValues.filter(
                          (v) => v.value.id !== item.value.id
                        )

                        const oldValue = contactCodes.find((v) => v.value.id === item.value.id)
                        if (
                          (oldValue && !oldValue.status) ||
                          (item.status === 'Rejected' && item.action === 'Delete')
                        ) {
                          if (oldValue) {
                            tempCurrentValues.push({
                              ...oldValue,
                              status: '',
                              action: '',
                            })
                          }
                        }

                        setCurrentValues(tempCurrentValues)
                      }}
                    >
                      Remove Request
                    </Button>
                  ) : null}
                  {item.status && item.status !== 'Done' ? (
                    <Tooltip
                      tipContent={() => (
                        <>{(item.status !== 'Rejected' ? '' : item.comment) + ' ' + `${label}`}</>
                      )}
                      horizontalStartPosition={TPopupHorizontal.Left}
                      horizontalDirection={TPopupHorizontal.Left}
                    >
                      {item.status === 'WIP' ? (
                        <i
                          className={classNames(
                            styles.icon,
                            styles['icon--wip'],
                            'kbase kbase-lvl-clock'
                          )}
                        />
                      ) : item.status === 'New' ? (
                        <i
                          className={classNames(
                            styles.icon,
                            styles['icon--new'],
                            'kbase kbase-add'
                          )}
                        />
                      ) : (
                        <i
                          className={classNames(
                            styles.icon,
                            styles['icon--rejected'],
                            'kbase kbase-info-alt'
                          )}
                        />
                      )}
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

interface IListPossibleProps {
  possibleValues: { id: string; disabled: boolean }[]
  filterAV: string
  errors: { key: string; value: string }[]
  setErrors: (val: { key: string; value: string }[]) => void
  readOnly: boolean
  isSuperAdmin: boolean
  selectedAV: ISelectedValue[]
  setSelectedAV: (val: ISelectedValue[]) => void
  HPID?: string
  checkFn?: (val: string) => string
  setShowDialog: (val: boolean) => void
}
const ListPossible = ({
  possibleValues,
  filterAV,
  errors,
  setErrors,
  readOnly,
  isSuperAdmin,
  selectedAV,
  setSelectedAV,
  HPID,
  checkFn,
  setShowDialog,
}: IListPossibleProps) => {
  return (
    <div className={styles.list}>
      <div className={styles.list__scroller}>
        {possibleValues
          .filter((item) =>
            filterAV ? ~item.id.toUpperCase().indexOf(filterAV.toUpperCase()) : true
          )
          .map((item, index) => {
            const err = errors.find((el) => el.key === item.id) || ''
            return (
              <div className={classNames(styles.item, styles['item--striped'])} key={item.id}>
                <div className={styles.item__check}>
                  <Checkbox
                    disabled={readOnly || (!isSuperAdmin && item.disabled)}
                    checked={!!selectedAV.find((v) => v.id === item.id && v.checked)}
                    onChange={() => {
                      if (
                        !TOOLS_REQUIRED_HPID.includes(item.id) ||
                        (TOOLS_REQUIRED_HPID.includes(item.id) && HPID)
                      ) {
                        const newArray = selectedAV.filter((v) => v.id !== item.id)
                        const el = selectedAV.find((v) => v.id === item.id)
                        if (el) {
                          if (checkFn) {
                            const checked = el.checked
                            const err = checkFn(item.id)
                            if (err) {
                              const idx = errors.findIndex((e) => e.key === item.id)
                              if (idx === -1 && !checked) {
                                setErrors([...errors, { key: item.id, value: err }])
                              }
                              if (idx > -1 && checked) {
                                errors.splice(idx, 1)
                                setErrors([...errors])
                              }
                            }
                          }
                          el.checked = !el.checked
                          newArray.push(el)
                          setSelectedAV(newArray)
                        }
                      }
                      if (TOOLS_REQUIRED_HPID.includes(item.id) && !HPID) {
                        setShowDialog(true)
                      }
                    }}
                  />
                </div>
                <div className={styles.item__label}>
                  {err ? (
                    <Tooltip tipContent={() => <>{err.value}</>}>
                      <span className={classNames(styles.ellipsis, styles.error)}>{item.id}</span>
                    </Tooltip>
                  ) : (
                    <span className={styles.ellipsis}>{item.id}</span>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}
export default VariantSelector
